import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from 'config/axiosConfig';
import { User } from 'util/constants';
import { isMobile, isBrowser } from 'react-device-detect';
import AuthService from 'services/authService';
import Cookies from 'js-cookie';

interface UserContextProps {
  currentUser: User | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
  awaitingUser: boolean;
  showAuthModal: boolean;
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;
  showUsernameModal: boolean;
  setShowUsernameModal: React.Dispatch<React.SetStateAction<boolean>>;
  showUserOnboardModal: boolean;
  setShowUserOnboardModal: React.Dispatch<React.SetStateAction<boolean>>;
  userOnboardingTasks: number | null;
  setUserOnboardingTasks: React.Dispatch<React.SetStateAction<number | null>>;
  refreshUser: () => Promise<void>;
  userIsMobile: boolean;
  userIsBrowser: boolean;
}

interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [awaitingUser, setAwaitingUser] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userIsMobile, setUserIsMobile] = useState(false);
  const [userIsBrowser, setUserIsBrowser] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showUserOnboardModal, setShowUserOnboardModal] = useState(false);
  const [userOnboardingTasks, setUserOnboardingTasks] = useState<number | null>(null);
  const fetchUser = async () => {
    try {
      const response = await api.get('/api/user');
      const userData = {
        ...response.data,
        id: parseInt(response.data.id, 10)
      };
      setCurrentUser(userData);
    } catch (error: any) {
      // Only clear current user if it's an auth error
      if (error.response?.status === 401) {
        setCurrentUser(null);
      }
    } finally {
      setAwaitingUser(false);
    }
  };

  const refreshUser = async () => {
    setAwaitingUser(true);
    await fetchUser();
  };

  useEffect(() => {
    if (isMobile) {
      setUserIsMobile(true);
    } else if (isBrowser) {
      setUserIsBrowser(true);
    }
  }, []);

  useEffect(() => {
    const cookieValue = Cookies.get('userOnboardingTasks');
    if (cookieValue !== undefined) {
        setUserOnboardingTasks(parseInt(cookieValue, 10));
    }
}, []);

  // Initial auth check
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Only try to refresh if we have a refresh token cookie
        const cookies = document.cookie.split(';');
        const hasRefreshToken = cookies.some(cookie => 
          cookie.trim().startsWith('refresh_token=')
        );

        if (hasRefreshToken) {
          await AuthService.refresh();
        }
      } catch (error) {
        // If refresh fails, we'll proceed to fetch user anyway
        console.error('Error refreshing token:', error);
      } finally {
        // Always try to fetch user info
        await fetchUser();
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (currentUser) {
      setAwaitingUser(false);
      setShowAuthModal(false);
    }
  }, [currentUser]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        awaitingUser,
        showAuthModal,
        setShowAuthModal,
        showUsernameModal,
        setShowUsernameModal,
        showUserOnboardModal,
        setShowUserOnboardModal,
        userOnboardingTasks,
        setUserOnboardingTasks,
        refreshUser,
        userIsMobile,
        userIsBrowser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
